import React from 'react';
import { manualSessionActive } from '../hooks/useAuth';
import { User } from 'firebase/auth';
import { useToast } from './Toast';

interface AuthenticatedInputProps {
  user: User | null;
  userProfile: any;
  onSessionStart: () => Promise<boolean>;
  onShowAuthModal: () => void;
  type?: 'number' | 'text';
  value: number | string | null;
  onChange: (value: number | string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

const AuthenticatedInput: React.FC<AuthenticatedInputProps> = ({
  user,
  userProfile,
  onSessionStart,
  onShowAuthModal,
  type = 'number',
  value,
  onChange,
  placeholder,
  className = '',
  disabled = false,
  min,
  max,
  step
}) => {
  const { showToast } = useToast();

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user) {
      onShowAuthModal();
      return;
    }

    if (!userProfile) return;

    // Read the value BEFORE any await: while the session write is in flight React
    // restores the controlled input, and reading it afterwards lost the first edit.
    const inputValue = e.target.value;

    // Start a manual session unless one is open (expired ones count as closed)
    if (!manualSessionActive(userProfile)) {
      const canStartSession = await onSessionStart();
      if (!canStartSession) {
        showToast(`You've reached your monthly limit of 3 manual input sessions. ${userProfile.subscriptionTier === 'free' ? 'Upgrade to Premium for unlimited access.' : ''}`, { type: 'warning' });
        return;
      }
    }

    if (type === 'number') {
      if (inputValue === '') {
        onChange(null);
        return;
      }
      let num = Number(inputValue);
      if (isNaN(num)) return; // ignore non-numeric input
      // Clamp to the field's valid range so out-of-range values (e.g. a typo
      // like 5000°) can't silently flow into the AMA impairment calculations
      if (min !== undefined && num < min) num = min;
      if (max !== undefined && num > max) num = max;
      onChange(num);
    } else {
      onChange(inputValue === '' ? null : inputValue);
    }
  };

  const isDisabled = !user || disabled;

  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={handleChange}
      aria-label={placeholder || 'measurement'}
      placeholder={!user ? 'Sign in required' : placeholder}
      className={`${className || 'input-field text-center'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      disabled={isDisabled}
      min={min}
      max={max}
      step={step}
    />
  );
};

export default AuthenticatedInput;
